import { useDisclosureWithContext, type UseDisclosureWithContextReturn } from '@talos/kyoko';
import { createContext, useCallback, useContext, useMemo } from 'react';

interface SettleBatchContextType {
  openBatchDetails: (options: OpenBatchDetailsOptions) => void;
  disclosure: UseDisclosureWithContextReturn<BatchDetailsDisclosureContext>;
}

export type BatchDetailsDisclosureContext = {
  batchID: string;
  instructionID?: string;
  onClickClose: () => void;
  onCloseComplete: () => void;
};

export type OpenBatchDetailsOptions = {
  batchID: string;
  instructionID?: string;
};

const SettleBatchContext = createContext<SettleBatchContextType | undefined>(undefined);

interface SettleBatchProviderProps {
  children: React.ReactNode;
}

export function SettleBatchProvider({ children }: SettleBatchProviderProps) {
  const disclosure = useDisclosureWithContext<BatchDetailsDisclosureContext>();
  const { close, open, onCloseComplete } = disclosure;

  const handleClose = useCallback(() => {
    close();
  }, [close]);

  const openBatchDetails = useCallback(
    (options: OpenBatchDetailsOptions) => {
      open({
        batchID: options.batchID,
        instructionID: options.instructionID,
        onClickClose: handleClose,
        onCloseComplete,
      });
    },
    [open, handleClose, onCloseComplete]
  );

  const value = useMemo(
    () => ({
      openBatchDetails,
      disclosure,
    }),
    [openBatchDetails, disclosure]
  );

  return <SettleBatchContext.Provider value={value}>{children}</SettleBatchContext.Provider>;
}

export function useSettleBatchContext() {
  const context = useContext(SettleBatchContext);
  if (context === undefined) {
    throw new Error('useSettleBatchContext must be used within SettleBatchProvider');
  }
  return context;
}
